import {
  BadgeCheck,
  Bell,
  ChevronsUpDown,
  CreditCard,
  LogOut,
  Moon,
  Sun,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useDescope, useSession, useUser } from "@descope/react-sdk";
import { useCallback, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { signOutOfAllCells } from "@/lib/cells/cell-session";
import { Skeleton } from "@/components/ui/skeleton";
import { useTheme } from "@/lib/theme/use-theme";
import { Switch } from "@/components/ui/switch";

export function UserSidebarMenu() {
  const navigate = useNavigate();
  const { isMobile } = useSidebar();
  const { theme, setTheme } = useTheme();

  const { logout } = useDescope();
  const { user: descopeUser, isUserLoading } = useUser();
  const { isSessionLoading } = useSession();

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Hack: the UserResponse type from descope is wrong, it can be undefined
  const user = descopeUser as typeof descopeUser | undefined;

  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await Promise.all([logout(), signOutOfAllCells()]);
      void navigate({ to: "/login" });
    } catch (error) {
      console.error("Logout failed:", error);
      void navigate({ to: "/login" });
    } finally {
      setIsLoggingOut(false);
    }
  }, [logout, navigate]);

  const userAvatarFallback =
    (user?.givenName?.charAt(0).toUpperCase() ?? "") +
      (user?.familyName?.charAt(0).toUpperCase() ?? "") ||
    user?.email?.charAt(0).toUpperCase();

  return (
    <SidebarMenu>
      {isUserLoading || isSessionLoading ? (
        <SidebarMenuItem>
          <Skeleton className="h-12 w-full" />
        </SidebarMenuItem>
      ) : (
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="rounded-lg">
                    {userAvatarFallback}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user?.name}</span>
                  <span className="truncate text-xs">{user?.email}</span>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="rounded-lg">
                      {userAvatarFallback}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{user?.name}</span>
                    <span className="truncate text-xs">{user?.email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem>
                  <BadgeCheck />
                  Account
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <CreditCard />
                  Billing
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Bell />
                  Notifications
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer justify-between px-2 py-1.5">
                  <div className="flex items-center gap-2">
                    {theme === "dark" ? <Moon className="size-4" /> : <Sun className="size-4" />}
                    <span>Theme</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground capitalize">{theme}</span>
                    <Switch
                      checked={theme === "dark"}
                      onCheckedChange={(checked) => {
                        setTheme(checked ? "dark" : "light");
                      }}
                    />
                  </div>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={isLoggingOut}
                onClick={() => {
                  void handleLogout();
                }}
              >
                <LogOut />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      )}
    </SidebarMenu>
  );
}
